import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Grid,
  Alert,
} from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import { getErrorMessage } from "utils/errorHelpers";
import { ProductPlatform } from "client/api-v2";
import type { Plan } from "client/api-v2";
import { billableTiers } from "../utils/plan-utils";
import { PlanSelector } from "./shared/PlanSelector";

interface ChangePlanFormData {
  plan?: Plan;
}

interface ChangePlanDialogProps {
  open: boolean;
  subscriptionName: string;
  currentPlan?: Plan;
  hasEntitlementOverrides: boolean;
  /** The cloud instance's own overrides could not be read, so hasEntitlementOverrides may understate them. */
  cloudOverridesUnknown?: boolean;
  handleClose: () => void;
  submitChange: (planId: string, clearEntitlementOverrides: boolean) => Promise<void>;
}

export function ChangePlanDialog({
  open,
  subscriptionName,
  currentPlan,
  hasEntitlementOverrides,
  cloudOverridesUnknown = false,
  handleClose,
  submitChange,
}: ChangePlanDialogProps) {
  const { control, reset } = useForm<ChangePlanFormData>({ defaultValues: { plan: undefined } });
  const selectedPlan = useWatch({ control, name: "plan" });
  const [clearOverrides, setClearOverrides] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset({ plan: undefined });
      setClearOverrides(true);
      setError(undefined);
    }
  }, [open, reset]);

  const isValid = useMemo(() => !!selectedPlan?.id && selectedPlan.id !== currentPlan?.id, [selectedPlan, currentPlan]);

  const isCloudPlan = currentPlan?.productPlatform === ProductPlatform.OctopusCloud;

  // The plan itself says so, rather than the tier being re-derived here: the backend clears for such a
  // plan whatever the request asks, because an override there can only reduce what the customer was
  // moved onto.
  const planAlwaysClearsOverrides = selectedPlan?.clearsEntitlementOverrides === true;
  const willClearOverrides = planAlwaysClearsOverrides || clearOverrides;

  // Offer the choice when the instance's overrides could not be read, rather than applying the default
  // to something the operator was never shown.
  const overridesUnknown = isCloudPlan && cloudOverridesUnknown;
  const showClearOverrides = hasEntitlementOverrides || overridesUnknown;

  const handleSubmit = async () => {
    if (!selectedPlan?.id) return;

    setIsSubmitting(true);
    setError(undefined);

    try {
      // Sent unconditionally rather than gated on hasEntitlementOverrides: that flag comes from a cloud
      // fetch that can fail silently, and a failed fetch must not quietly turn the operator's choice off.
      // Clearing when there is nothing to clear is a no-op on both platforms.
      await submitChange(selectedPlan.id, willClearOverrides);
      handleClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Change Plan</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Box sx={{ mb: 1, mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Subscription:</strong> {subscriptionName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Current Plan:</strong> {currentPlan?.name ?? "Unknown"}
              </Typography>
            </Box>
          </Grid>

          <Grid size={12}>
            <Alert severity="warning">
              {isCloudPlan
                ? "Entitlements and the license key are regenerated immediately, and the new limits are pushed to the running instance — task cap, storage and retention all change straight away."
                : "Entitlements and the license key will be regenerated immediately."}{" "}
              Billing in Zuora is <strong>not</strong> changed — the customer will continue to be billed for their
              existing Zuora product, and the next Zuora renewal or amendment will reset the plan to the one derived
              from Zuora, so a manual plan change may need re-applying after billing events.
            </Alert>
          </Grid>

          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          <Grid size={12}>
            <PlanSelector
              name="plan"
              label="New Plan"
              required
              control={control}
              platform={currentPlan?.productPlatform}
              allowedTiers={billableTiers}
            />
            {selectedPlan?.id === currentPlan?.id && selectedPlan && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The subscription is already on this plan.
              </Typography>
            )}
          </Grid>

          {showClearOverrides && (
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={willClearOverrides}
                    onChange={(e) => setClearOverrides(e.target.checked)}
                    disabled={isSubmitting || planAlwaysClearsOverrides}
                  />
                }
                label="Clear entitlement overrides"
              />
              <Typography variant="body2" color="text.secondary">
                {hasEntitlementOverrides
                  ? "This subscription has entitlement overrides. When cleared, entitlements come entirely from the new plan; otherwise the current overrides are preserved and continue to take precedence over the plan."
                  : "The cloud instance could not be read, so whether it carries overrides is unknown. When cleared, entitlements come entirely from the new plan; otherwise any existing overrides are preserved and continue to take precedence over the plan."}
              </Typography>
              {planAlwaysClearsOverrides && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedPlan?.name ?? "This plan"} grants the maximum of every limit, so overrides are always cleared
                  when moving onto it.
                </Typography>
              )}
              {isCloudPlan && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  A staff-specified task cap and a customer-specified machine limit are held on the cloud instance
                  rather than in the plan. Clearing overrides drops both, so the instance falls back to the task cap and
                  machine limit the new plan includes.
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !isValid}>
          {isSubmitting ? "Changing..." : "Change Plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
